/**
 * A map card's thumbnail (spec 025 D-5): the scene's background image; else
 * the background colour with its first character's picture on it; else a
 * coloured tile with the scene's initial. Images come through the editor's
 * preview media loader (`loadPreviewImage`, 009): one decode per media id,
 * no runtime and no hidden players.
 */

import { useEffect, useMemo, useState } from 'react';
import type { StoryApiResponse } from '../../../../Shared/src/@types';
import { apiClient } from '../../api/client';
import { useSceneStore } from '../../stores/sceneStore';
import { loadPreviewImage } from '../../utils/previewMedia';
import { loadPreviewScene } from '../../utils/previewSources';

export type Thumb =
  | { kind: 'image'; mediaId: number; color: string }
  | { kind: 'character'; mediaId: number; color: string }
  | { kind: 'tile'; color: string; initial: string };

/** What a thumbnail needs from a scene. */
export interface ThumbScene {
  background?: { baseMediaId?: number; color?: string } | null;
  sprites?: ReadonlyArray<{ defaultState?: string; states?: ReadonlyArray<{ name: string; mediaId?: number }> }> | null;
}

const TILE_COLOURS = ['#5a3d2b', '#2b4a5a', '#3d5a2b', '#5a2b4a', '#4a4a2b', '#2b5a4f'];

function tileColour(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TILE_COLOURS[h % TILE_COLOURS.length];
}

/** The D-5 rule, pure. */
export function thumbFor(scene: ThumbScene | null | undefined, name: string, fallbackColour?: string | null): Thumb {
  const color = scene?.background?.color || fallbackColour || tileColour(name);
  const bg = scene?.background?.baseMediaId;
  if (bg) return { kind: 'image', mediaId: bg, color };
  const sprite = scene?.sprites?.[0];
  const state = sprite?.states?.find((s) => s.name === sprite.defaultState) ?? sprite?.states?.[0];
  if (state?.mediaId) return { kind: 'character', mediaId: state.mediaId, color };
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  return { kind: 'tile', color: scene?.background?.color || fallbackColour || tileColour(name), initial };
}

/**
 * Thumbnails for the story's scenes, by `StoryScene.id`. Each scene is read
 * once per listing change (`loadPreviewScene`: the open scene, its draft, or
 * `GET /scenes/:id`); the open scene follows the scene store live.
 */
export function useSceneThumbs(story: Pick<StoryApiResponse, 'storyScenes'>, extraMediaIds: readonly number[] = []): {
  thumbs: Map<number, Thumb>;
  srcFor: (mediaId: number) => string | undefined;
} {
  const [scenes, setScenes] = useState<Record<number, ThumbScene | null>>({});
  const [srcs, setSrcs] = useState<Record<number, string>>({});
  const openScene = useSceneStore((s) => s.scene) as unknown as (ThumbScene & { id?: unknown }) | null;

  // Re-read a scene when its listing row changes (a turn applied, a rename).
  const rowsKey = story.storyScenes
    .map((ss) => `${ss.id}:${ss.sceneId}:${ss.updatedAt}:${ss.sceneName ?? ''}:${ss.backgroundColour ?? ''}`)
    .join('|');
  useEffect(() => {
    let cancelled = false;
    for (const ss of story.storyScenes) {
      void loadPreviewScene(ss.sceneId).then((scene) => {
        if (cancelled) return;
        setScenes((prev) => ({ ...prev, [ss.sceneId]: (scene as unknown as ThumbScene) ?? null }));
      });
    }
    return () => { cancelled = true; };
    // rowsKey stands for the listing rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey]);

  const thumbs = useMemo(() => {
    const out = new Map<number, Thumb>();
    for (const ss of story.storyScenes) {
      const live = openScene && Number(openScene.id) === ss.sceneId ? openScene : undefined;
      const name = ss.sceneName || `Scene #${ss.sceneId}`;
      out.set(ss.id, thumbFor(live ?? scenes[ss.sceneId], name, ss.backgroundColour));
    }
    return out;
  }, [story.storyScenes, scenes, openScene]);

  // Decode each image once (the loader caches per media id); extra ids are the open scene's pictures.
  const ids = [...new Set([
    ...[...thumbs.values()].flatMap((t) => (t.kind === 'tile' ? [] : [t.mediaId])),
    ...extraMediaIds,
  ])];
  const mediaKey = ids.join(',');
  useEffect(() => {
    let cancelled = false;
    for (const id of ids) {
      if (srcs[id]) continue;
      void loadPreviewImage(apiClient, id).then((img) => {
        if (!cancelled && img?.src) setSrcs((prev) => (prev[id] ? prev : { ...prev, [id]: img.src }));
      });
    }
    return () => { cancelled = true; };
    // mediaKey stands for the thumbnails' media ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaKey]);

  return { thumbs, srcFor: (id) => srcs[id] };
}
